import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarCheck, Clock, Fish, Info, MapPin, PawPrint, Shield } from "lucide-react";
import { PARK } from "@/lib/site";
import Footer from "@/components/Footer";
import SiteMap from "@/components/SiteMap";
import { LuxeOverline } from "@/components/LuxeOverline";
import Seo from "@/components/Seo";

const SECTIONS = [
  {
    id: "booking",
    icon: Shield,
    title: "Booking & payment",
    rules: [
      "Booking more than 14 days ahead? A 25% deposit secures your dates — the balance is settled on arrival.",
      "Booking within 14 days of arrival? Full payment is taken up front for a smooth, keys-in-hand check-in.",
      "Your booking is confirmed by email the moment payment clears — keep the confirmation for your records.",
      "Lodge stays: a refundable £100 security deposit is held on a card of your choice when you arrive (a pre-authorisation, not a charge) and released after your departure check — only charged in the event of damage or extra cleaning. No deposit applies to camping pitches.",
    ],
  },
  {
    id: "checkin",
    icon: Clock,
    title: "Check-in & check-out",
    rules: [
      "Check-in: camping pitches from 1:00 PM, lodges from 2:00 PM.",
      "Check-out is by 11:00 AM for every pitch and lodge.",
      "Latest arrival time is 8:00 PM — if you're delayed, call or email us and we'll do our best to make arrangements.",
    ],
  },
  {
    id: "cancellation",
    icon: CalendarCheck,
    title: "Cancellations & refunds",
    rules: [
      "72-hour change of mind — cancel within 72 hours of booking for a full refund, no questions asked.",
      "More than 14 days before arrival — full refund, minus a 10% admin fee.",
      "Within 14 days of arrival — a 12-month credit voucher (or a 50% refund at our discretion).",
      "Unforeseen circumstances — full refund or free rescheduling for vehicle breakdown (AA/RAC receipt), illness (doctor's note), bereavement, or any other genuine emergency at our discretion.",
    ],
  },
  {
    id: "dogs",
    icon: PawPrint,
    title: "Dogs",
    rules: [
      "Dogs are welcome in our lakeside lodges — maximum 2 dogs per lodge. No dangerous breeds.",
      "Dog cleaning fee per stay: £30 for one dog, £45 for two dogs.",
      "Dogs must be kept on a lead at all times anywhere on the park.",
      "Please bring your own dog bed and blanket — our lodge bedding is for humans.",
      "Dogs are not allowed on the furniture. If yours does hop up, the furniture must be covered with your own blanket first.",
      "There's a half-mile enclosed dog walk on site for off-lead exercise.",
      "When you book online with dogs, you'll be asked to agree to these rules before payment.",
    ],
  },
  {
    id: "fishing",
    icon: Fish,
    title: "Fishing",
    rules: [
      "Fishing is for staying residents only — we do not offer day fishing and we do not host matches.",
      "Lodge guests fish free with one complimentary rod per person per stay; additional rods are £6 each per day.",
      "Camping guests: £6 per day for one rod, £10 per day for two rods.",
      "Every angler aged 13 or over must hold a valid Environment Agency rod licence and be able to show it on request.",
      "Barbless hooks only — no micro-barbs. Unhooking mats and landing nets are required.",
      "No boilies larger than 15mm. No nut baits or bloodworm.",
    ],
  },
  {
    id: "general",
    icon: Info,
    title: "Good to know",
    rules: [
      "Walnut Lakes is an adults-only park — all guests must be aged 15 or over.",
      `Our season runs ${PARK.season}.`,
      "Pitches with electric hook-up (EHU) include 15 kWh of electricity per day — anything beyond that is chargeable at 33p per kWh using a prepaid top-up card.",
      "Specific pitches are allocated on arrival — tell us your setup when booking and we'll match you to the right spot.",
    ],
  },
];

const PoliciesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="policies-page">
      <Seo
        title="Park Policies"
        description="Booking, cancellation, dog and fishing policies for Walnut Lakes Holiday Park — an adults-only holiday park near Boston, Lincolnshire."
        path="/policies"
      />
      {/* Slim top bar */}
      <header className="border-b border-[#DDD1BE] bg-[#F5EFE6]/90 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="policies-logo">
            <img src={PARK.logo} alt="Walnut Lakes" className="h-12 w-auto logo-blend" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#6A5F58] hover:text-[#0F0E0E] transition-colors"
            data-testid="policies-back-home"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <LuxeOverline>{PARK.fullName}</LuxeOverline>
        <h1 className="font-serif text-4xl md:text-6xl mt-6 text-[#14100F] leading-[1.05]">
          Park <em className="italic rose-gold-text">policies</em>
        </h1>
        <p className="mt-6 max-w-2xl text-[#3A322E] text-base md:text-lg leading-relaxed">
          Everything in one place — how booking and payment works, when you can arrive,
          what happens if plans change, and the house rules that keep the park peaceful
          for dogs, anglers and everyone in between.
        </p>

        <div className="mt-14 space-y-8">
          {/* Site map — guests can point at a favourite pitch when they call */}
          <section id="site-map" data-testid="policy-section-sitemap"
            className="relative p-8 md:p-10 bg-white border border-[#DDD1BE] rounded-2xl shadow-sm overflow-hidden">
            <span className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#B76E79] via-[#E4B2A8] to-[#C9A47E]" />
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F5EFE6] to-[#E4B2A8] flex items-center justify-center border border-[#B76E79]/30">
                <MapPin size={16} className="text-[#0F0E0E]" strokeWidth={1.4} />
              </span>
              <h2 className="font-serif text-2xl md:text-3xl text-[#0F0E0E]">Site map</h2>
            </div>
            <p className="mt-4 text-sm text-[#3A322E] leading-relaxed max-w-2xl">
              Two lakes, three lodges and 47 pitches. We never allocate pitch numbers in advance — but if
              one of these spots is your favourite, mention it when you book or give us a call and we'll
              reserve it for you if it's free.
            </p>
            <div className="mt-6">
              <SiteMap />
            </div>
          </section>

          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`policy-section-${s.id}`}
              className="relative p-8 md:p-10 bg-white border border-[#DDD1BE] rounded-2xl shadow-sm overflow-hidden"
            >
              <span className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#B76E79] via-[#E4B2A8] to-[#C9A47E]" />
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F5EFE6] to-[#E4B2A8] flex items-center justify-center border border-[#B76E79]/30">
                  <s.icon size={16} className="text-[#0F0E0E]" strokeWidth={1.4} />
                </span>
                <h2 className="font-serif text-2xl md:text-3xl text-[#0F0E0E]">{s.title}</h2>
              </div>
              <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                {s.rules.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-sm text-[#3A322E] leading-relaxed">
                    <span className="mt-2 w-1.5 h-1.5 rotate-45 bg-[#B76E79] flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-2xl border border-[#DDD1BE] bg-white p-8 md:p-10">
          <div>
            <p className="font-serif text-2xl text-[#0F0E0E]">Happy with the house rules?</p>
            <p className="mt-2 text-sm text-[#6A5F58]">
              Questions first? Call {PARK.phone} or email {PARK.email} — we're glad to help.
            </p>
          </div>
          <Link
            to="/#book"
            className="btn-luxe-xl font-brand tracking-[0.18em] whitespace-nowrap"
            data-testid="policies-book-cta"
          >
            Book your stay
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PoliciesPage;
